import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  addScheme,
  updateScheme,
  createYojanaApproval,
  updateYojanaApproval,
  fetchStates,
  fetchDistrictsByState,
  fetchTalukasByDistrict,
  fetchVillagesByTaluka,
  fetchReligions,
  fetchCastCategories,
  fetchCasts,
  fetchEducation,
  fetchProfessions,
  fetchYojanaCategories,
  uploadYojanaVideo
} from '../../utils/api';
import {
  INCOME_RANGE_OPTIONS,
  getIncomeRange,
  parseIncomeRange
} from '../../utils/incomeRange';
import { usePermissions } from '../../hooks/usePermissions';

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];
const RATION_CARD_OPTIONS = ['Yellow', 'White', 'Orange'];

const EMPTY_FILTER_FORM = {
  minAge: '',
  maxAge: '',
  religion: null,
  cast: null,
  castCategory: null,
  state: null,
  district: null,
  taluka: null,
  village: null,
  education: null,
  incomeRange: null,
  minFarm: '',
  maxFarm: '',
  rationCard: null,
  gender: null,
  minDisability: '',
  maxDisability: '',
  professions: []
};

const parseNumOrNull = (value) => {
  if (value === '' || value == null) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const initFilterFormFromSource = (source) => {
  const filters = source?.filters;
  if (!filters) return { ...EMPTY_FILTER_FORM };

  return {
    minAge: filters.minAge ?? '',
    maxAge: filters.maxAge ?? '',
    religion: filters.religion || null,
    cast: filters.cast || null,
    castCategory: filters.castCategory || null,
    state: filters.state || null,
    district: filters.district || null,
    taluka: filters.taluka || null,
    village: filters.village || null,
    education: filters.education || null,
    incomeRange: getIncomeRange(filters.minIncome, filters.maxIncome),
    minFarm: filters.minFarm ?? '',
    maxFarm: filters.maxFarm ?? '',
    rationCard: filters.rationCard || null,
    gender: filters.gender || null,
    minDisability: filters.minDisability ?? '',
    maxDisability: filters.maxDisability ?? '',
    professions: Array.isArray(filters.profession) ? filters.profession : []
  };
};

const buildFilters = (filterForm, existingFilters) => {
  const { minIncome, maxIncome } = parseIncomeRange(filterForm.incomeRange);

  const filters = {
    minAge: parseNumOrNull(filterForm.minAge),
    maxAge: parseNumOrNull(filterForm.maxAge),
    religion: filterForm.religion?.religionId != null
      ? { religionId: filterForm.religion.religionId }
      : null,
    cast: filterForm.cast?.castId != null
      ? { castId: filterForm.cast.castId }
      : null,
    castCategory: filterForm.castCategory?.categoryId != null
      ? { categoryId: filterForm.castCategory.categoryId }
      : null,
    state: filterForm.state?.stateId != null
      ? { stateId: filterForm.state.stateId }
      : null,
    district: filterForm.district?.districtId != null
      ? { districtId: filterForm.district.districtId }
      : null,
    taluka: filterForm.taluka?.talukaId != null
      ? { talukaId: filterForm.taluka.talukaId }
      : null,
    village: filterForm.village?.villageId != null
      ? { villageId: filterForm.village.villageId }
      : null,
    education: filterForm.education?.educationId != null
      ? { educationId: filterForm.education.educationId }
      : null,
    minIncome,
    maxIncome,
    minFarm: parseNumOrNull(filterForm.minFarm),
    maxFarm: parseNumOrNull(filterForm.maxFarm),
    rationCard: filterForm.rationCard || null,
    gender: filterForm.gender || null,
    minDisability: parseNumOrNull(filterForm.minDisability),
    maxDisability: parseNumOrNull(filterForm.maxDisability),
    profession: filterForm.professions?.length
      ? filterForm.professions.map((p) => ({ professionId: p.professionId ?? p.id }))
      : []
  };

  if (existingFilters?.yojnaFilterId != null) {
    filters.yojnaFilterId = existingFilters.yojnaFilterId;
  }

  return filters;
};

const getOptionId = (option, idKeys) => {
  if (!option) return null;
  for (const key of idKeys) {
    if (option[key] != null) return option[key];
  }
  return option.id ?? null;
};

const SchemeForm = ({ scheme = null, prefillDraft = null, onSuccess }) => {
  const { checkPermission, isYojanaAdmin } = usePermissions();
  const fieldSource = scheme || prefillDraft;
  const existingFilters = fieldSource?.filters || null;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingGrLink, setUploadingGrLink] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [videoFile, setVideoFile] = useState(null);
  const [videoUploadError, setVideoUploadError] = useState(null);
  const [grLinkFile, setGrLinkFile] = useState(null);
  const [grLinkUploadError, setGrLinkUploadError] = useState(null);
  const [grLinkRemoved, setGrLinkRemoved] = useState(false);
  const [formPdfUrlFile, setFormPdfUrlFile] = useState(null);
  const [formPdfUrlUploadError, setFormPdfUrlUploadError] = useState(null);
  const [formPdfUrlRemoved, setFormPdfUrlRemoved] = useState(false);
  const [uploadingFormPdfUrl, setUploadingFormPdfUrl] = useState(false);

  const [filterForm, setFilterForm] = useState(() => initFilterFormFromSource(fieldSource));

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);
  const [religions, setReligions] = useState([]);
  const [castCategories, setCastCategories] = useState([]);
  const [casts, setCasts] = useState([]);
  const [educationList, setEducationList] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [categories, setCategories] = useState([]);

  const [isLoadingStates, setIsLoadingStates] = useState(true);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingTalukas, setIsLoadingTalukas] = useState(false);
  const [isLoadingVillages, setIsLoadingVillages] = useState(false);
  const [isLoadingCasts, setIsLoadingCasts] = useState(true);
  const [isLoadingReligions, setIsLoadingReligions] = useState(true);
  const [isLoadingEducation, setIsLoadingEducation] = useState(true);
  const [isLoadingProfessions, setIsLoadingProfessions] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  useEffect(() => {
    setFilterForm(initFilterFormFromSource(fieldSource));
  }, [scheme, prefillDraft]);

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      try {
        const [
          statesResponse,
          religionsResponse,
          castCategoriesResponse,
          castsResponse,
          educationResponse,
          professionsResponse,
          categoriesResponse
        ] = await Promise.all([
          fetchStates(),
          fetchReligions(),
          fetchCastCategories(),
          fetchCasts(),
          fetchEducation(),
          fetchProfessions(),
          fetchYojanaCategories()
        ]);

        if (!isMounted) return;

        setStates(statesResponse.data || []);
        setReligions(religionsResponse.data || []);
        setCastCategories(castCategoriesResponse.data || []);
        setCasts(castsResponse.data || []);
        setEducationList(educationResponse.data || []);
        setProfessions(professionsResponse.data || []);
        setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);

        setIsLoadingStates(false);
        setIsLoadingReligions(false);
        setIsLoadingCasts(false);
        setIsLoadingEducation(false);
        setIsLoadingProfessions(false);
        setIsLoadingCategories(false);

        const draftForDeps = scheme || prefillDraft;
        if (!draftForDeps?.filters) return;

        const { state, district, taluka } = draftForDeps.filters;
        const stateId = state?.stateId;
        const districtId = district?.districtId;
        const talukaId = taluka?.talukaId;

        if (stateId) {
          setIsLoadingDistricts(true);
          try {
            const districtsResponse = await fetchDistrictsByState(stateId);
            if (!isMounted) return;
            setDistricts(districtsResponse.data || []);

            if (districtId) {
              setIsLoadingTalukas(true);
              try {
                const talukasResponse = await fetchTalukasByDistrict(districtId);
                if (!isMounted) return;
                setTalukas(talukasResponse.data || []);

                if (talukaId) {
                  setIsLoadingVillages(true);
                  try {
                    const villagesResponse = await fetchVillagesByTaluka(talukaId);
                    if (!isMounted) return;
                    setVillages(villagesResponse.data || []);
                  } catch (error) {
                    console.error('Error loading villages:', error);
                    if (isMounted) setVillages([]);
                  } finally {
                    if (isMounted) setIsLoadingVillages(false);
                  }
                }
              } catch (error) {
                console.error('Error loading talukas:', error);
                if (isMounted) setTalukas([]);
              } finally {
                if (isMounted) setIsLoadingTalukas(false);
              }
            }
          } catch (error) {
            console.error('Error loading districts:', error);
            if (isMounted) setDistricts([]);
          } finally {
            if (isMounted) setIsLoadingDistricts(false);
          }
        }
      } catch (error) {
        console.error('Error loading form data:', error);
        if (isMounted) {
          setSnackbarMessage('Failed to load form data. Please refresh the page.');
          setIsLoadingCategories(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [scheme, prefillDraft]);

  useEffect(() => {
    if (!checkPermission('YOJNA', 'write')) {
      showSnackbar('You do not have permission to modify schemes', 'error');
      if (onSuccess) onSuccess();
    }
  }, []);

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required').max(600, 'Title must be at most 200 characters'),
    description: Yup.string().required('Description is required'),
    eligibility: Yup.string().required('Eligibility criteria is required'),
    termsConditions: Yup.string().required('Terms and conditions are required'),
    benefits: Yup.string().required('Benefits is required'),
    documents: Yup.string().required('Required documents are required'),
    category: Yup.object({
      categoryId: Yup.string().nullable().required('Category is required')
    }).required()
  });

  const initialValues = fieldSource ? {
    title: fieldSource.title || '',
    description: fieldSource.description || '',
    eligibility: fieldSource.eligibility || '',
    termsConditions: fieldSource.termsConditions || '',
    benefits: fieldSource.benefits || '',
    documents: fieldSource.documents || '',
    yojanaVideoUrl: fieldSource.yojanaVideoUrl || '',
    grLink: fieldSource.grLink || '',
    formPdfUrl: fieldSource.formPdfUrl || '',
    pdfFormLink: fieldSource.pdfFormLink || '',
    officeAddress: fieldSource.officeAddress || '',
    officeAddressContact: fieldSource.officeAddressContact || '',
    yojanaGovernment: fieldSource.yojanaGovernment || '',
    yojanaInformationCompleted: fieldSource.status === 'Completed',
    category: {
      categoryId: fieldSource.category?.categoryId?.toString?.() || null
    }
  } : {
    title: '',
    description: '',
    eligibility: '',
    termsConditions: '',
    benefits: '',
    documents: '',
    yojanaVideoUrl: '',
    grLink: '',
    formPdfUrl: '',
    pdfFormLink: '',
    officeAddress: '',
    officeAddressContact: '',
    yojanaGovernment: '',
    yojanaInformationCompleted: false,
    category: { categoryId: null }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const updateFilterForm = (updates) => {
    setFilterForm((prev) => ({ ...prev, ...updates }));
  };

  const handleFilterNumberChange = (field) => (event) => {
    updateFilterForm({ [field]: event.target.value });
  };

  const handleStateChange = async (event, newValue) => {
    updateFilterForm({
      state: newValue,
      district: null,
      taluka: null,
      village: null
    });
    setDistricts([]);
    setTalukas([]);
    setVillages([]);

    const stateId = newValue?.stateId;
    if (!stateId) return;

    setIsLoadingDistricts(true);
    try {
      const response = await fetchDistrictsByState(stateId);
      setDistricts(response.data || []);
    } catch (error) {
      console.error('Error loading districts:', error);
      setDistricts([]);
    } finally {
      setIsLoadingDistricts(false);
    }
  };

  const handleDistrictChange = async (event, newValue) => {
    updateFilterForm({
      district: newValue,
      taluka: null,
      village: null
    });
    setTalukas([]);
    setVillages([]);

    const districtId = newValue?.districtId;
    if (!districtId) return;

    setIsLoadingTalukas(true);
    try {
      const response = await fetchTalukasByDistrict(districtId);
      setTalukas(response.data || []);
    } catch (error) {
      console.error('Error loading talukas:', error);
      setTalukas([]);
    } finally {
      setIsLoadingTalukas(false);
    }
  };

  const handleTalukaChange = async (event, newValue) => {
    updateFilterForm({
      taluka: newValue,
      village: null
    });
    setVillages([]);

    const talukaId = newValue?.talukaId;
    if (!talukaId) return;

    setIsLoadingVillages(true);
    try {
      const response = await fetchVillagesByTaluka(talukaId);
      setVillages(response.data || []);
    } catch (error) {
      console.error('Error loading villages:', error);
      setVillages([]);
    } finally {
      setIsLoadingVillages(false);
    }
  };

  const handleVideoChange = (event) => {
    const file = event.currentTarget.files && event.currentTarget.files[0]
      ? event.currentTarget.files[0]
      : null;
    setVideoFile(file);
    setVideoUploadError(null);

    if (!file && !fieldSource?.yojanaVideoUrl) {
      formik.setFieldValue('yojanaVideoUrl', '');
    }
  };

  const handleGrLinkChange = (event) => {
    const file = event.currentTarget.files && event.currentTarget.files[0]
      ? event.currentTarget.files[0]
      : null;
    setGrLinkFile(file);
    setGrLinkUploadError(null);
    setGrLinkRemoved(false);
    if (!file && !fieldSource?.grLink) {
      formik.setFieldValue('grLink', '');
    }
  };

  const handleGrLinkRemove = () => {
    setGrLinkFile(null);
    setGrLinkUploadError(null);
    setGrLinkRemoved(true);
    formik.setFieldValue('grLink', '');
  };

  const handleFormPdfUrlChange = (event) => {
    const file = event.currentTarget.files && event.currentTarget.files[0]
      ? event.currentTarget.files[0]
      : null;
    setFormPdfUrlFile(file);
    setFormPdfUrlUploadError(null);
    setFormPdfUrlRemoved(false);
    if (!file && !fieldSource?.formPdfUrl) {
      formik.setFieldValue('formPdfUrl', '');
    }
  };

  const handleFormPdfUrlRemove = () => {
    setFormPdfUrlFile(null);
    setFormPdfUrlUploadError(null);
    setFormPdfUrlRemoved(true);
    formik.setFieldValue('formPdfUrl', '');
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setVideoUploadError(null);
      setGrLinkUploadError(null);
      setFormPdfUrlUploadError(null);

      try {
        let latestVideoUrl = values.yojanaVideoUrl || '';
        let latestGrLink = values.grLink || '';
        let latestFormPdfUrl = values.formPdfUrl || '';

        if (videoFile) {
          setUploadingVideo(true);
          try {
            const uploadResponse = await uploadYojanaVideo(videoFile);
            const responseData = uploadResponse?.data;
            const uploadedUrl =
              typeof responseData === 'string'
                ? responseData
                : responseData?.yojanaVideoUrl ||
                  responseData?.url ||
                  responseData?.location ||
                  '';

            if (!uploadedUrl) {
              throw new Error('Video upload did not return a valid URL.');
            }

            latestVideoUrl = uploadedUrl;
            formik.setFieldValue('yojanaVideoUrl', uploadedUrl);
          } catch (uploadError) {
            console.error('Error uploading yojana video:', uploadError);
            const message =
              uploadError.response?.data?.message ||
              uploadError.message ||
              'Failed to upload video. Please try again.';
            setVideoUploadError(message);
            showSnackbar(message, 'error');
            setUploadingVideo(false);
            setIsSubmitting(false);
            return;
          } finally {
            setUploadingVideo(false);
          }
        }

        if (grLinkFile) {
          setUploadingGrLink(true);
          try {
            const uploadResponse = await uploadYojanaVideo(grLinkFile);
            const responseData = uploadResponse?.data;
            const uploadedGrUrl =
              typeof responseData === 'string'
                ? responseData
                : responseData?.grLink ||
                  responseData?.yojanaVideoUrl ||
                  responseData?.url ||
                  responseData?.location ||
                  '';

            if (!uploadedGrUrl) {
              throw new Error('GR link file upload did not return a valid URL.');
            }

            latestGrLink = uploadedGrUrl;
            formik.setFieldValue('grLink', uploadedGrUrl);
          } catch (uploadError) {
            console.error('Error uploading GR link file:', uploadError);
            const message =
              uploadError.response?.data?.message ||
              uploadError.message ||
              'Failed to upload GR link file. Please try again.';
            setGrLinkUploadError(message);
            showSnackbar(message, 'error');
            setUploadingGrLink(false);
            setIsSubmitting(false);
            return;
          } finally {
            setUploadingGrLink(false);
          }
        }

        if (formPdfUrlFile) {
          setUploadingFormPdfUrl(true);
          try {
            const uploadResponse = await uploadYojanaVideo(formPdfUrlFile);
            const responseData = uploadResponse?.data;
            const uploadedFormPdfUrl =
              typeof responseData === 'string'
                ? responseData
                : responseData?.formPdfUrl ||
                  responseData?.grLink ||
                  responseData?.yojanaVideoUrl ||
                  responseData?.url ||
                  responseData?.location ||
                  '';

            if (!uploadedFormPdfUrl) {
              throw new Error('Form PDF file upload did not return a valid URL.');
            }

            latestFormPdfUrl = uploadedFormPdfUrl;
            formik.setFieldValue('formPdfUrl', uploadedFormPdfUrl);
          } catch (uploadError) {
            console.error('Error uploading form PDF file:', uploadError);
            const message =
              uploadError.response?.data?.message ||
              uploadError.message ||
              'Failed to upload form PDF file. Please try again.';
            setFormPdfUrlUploadError(message);
            showSnackbar(message, 'error');
            setUploadingFormPdfUrl(false);
            setIsSubmitting(false);
            return;
          } finally {
            setUploadingFormPdfUrl(false);
          }
        }

        const schemeData = {
          title: values.title,
          description: values.description,
          eligibility: values.eligibility,
          termsConditions: values.termsConditions,
          benefits: values.benefits,
          documents: values.documents,
          filters: buildFilters(filterForm, existingFilters)
        };

        if (values.category?.categoryId) {
          schemeData.category = {
            categoryId: values.category.categoryId
          };
        }
        if (values.yojanaGovernment) {
          schemeData.yojanaGovernment = values.yojanaGovernment;
        }
        schemeData.status = values.yojanaInformationCompleted ? 'Completed' : 'Incomplete';

        if (latestVideoUrl) {
          schemeData.yojanaVideoUrl = latestVideoUrl;
        }

        if ((scheme || prefillDraft) && (grLinkRemoved || latestGrLink === '')) {
          schemeData.grLink = null;
        } else if (latestGrLink) {
          schemeData.grLink = latestGrLink;
        }

        if ((scheme || prefillDraft) && (formPdfUrlRemoved || latestFormPdfUrl === '')) {
          schemeData.formPdfUrl = null;
        } else if (latestFormPdfUrl) {
          schemeData.formPdfUrl = latestFormPdfUrl;
        }
        const pdfLink = values.pdfFormLink != null ? String(values.pdfFormLink).trim() : '';
        schemeData.pdfFormLink = pdfLink || null;
        const officeAddr = values.officeAddress != null ? String(values.officeAddress).trim() : '';
        schemeData.officeAddress = officeAddr || null;
        const officeContact = values.officeAddressContact != null ? String(values.officeAddressContact).trim() : '';
        schemeData.officeAddressContact = officeContact || null;

        if (scheme?.serialNo != null) {
          schemeData.serialNo = scheme.serialNo;
        }

        const isAdmin = isYojanaAdmin();
        if (scheme) {
          if (isAdmin) {
            await updateScheme(scheme.yojnaId, schemeData, {
              previousData: scheme,
              identifier: scheme.title || schemeData.title
            });
            showSnackbar('Scheme updated successfully!');
          } else {
            await updateYojanaApproval(scheme.yojnaId, schemeData, {
              previousData: scheme,
              identifier: scheme.title || schemeData.title
            });
            showSnackbar('Update submitted for approval.');
          }
        } else {
          if (isAdmin) {
            await addScheme(schemeData, {
              identifier: schemeData.title
            });
            showSnackbar('Scheme added successfully!');
          } else {
            await createYojanaApproval(schemeData);
            showSnackbar('Scheme submitted for approval.');
          }
        }

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error saving scheme:', error);
        showSnackbar(error.response?.data?.message || 'Failed to save scheme. Please try again.', 'error');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name.includes('.')) {
      if (name === 'category.categoryId') {
        formik.setFieldValue('category.categoryId', value);
      } else {
        formik.setFieldValue(name, value);
      }
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const resolveSelectedOption = (options, selected, idKeys) => {
    if (!selected) return null;
    const selectedId = getOptionId(selected, idKeys);
    if (selectedId == null) return selected;
    return options.find((opt) => String(getOptionId(opt, idKeys)) === String(selectedId)) || selected;
  };

  const resolveSelectedOptions = (options, selectedList, idKeys) => {
    if (!Array.isArray(selectedList) || !selectedList.length) return [];
    return selectedList
      .map((selected) => resolveSelectedOption(options, selected, idKeys))
      .filter(Boolean);
  };

  if (isLoadingStates || isLoadingReligions || isLoadingProfessions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="title"
            name="title"
            label="Scheme Title"
            value={formik.values.title}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            multiline
            rows={4}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="eligibility"
            name="eligibility"
            label="Eligibility Criteria"
            value={formik.values.eligibility}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.eligibility && Boolean(formik.errors.eligibility)}
            helperText={formik.touched.eligibility && formik.errors.eligibility}
            multiline
            rows={3}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="termsConditions"
            name="termsConditions"
            label="Terms and Conditions"
            value={formik.values.termsConditions}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.termsConditions && Boolean(formik.errors.termsConditions)}
            helperText={formik.touched.termsConditions && formik.errors.termsConditions}
            multiline
            rows={3}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="benefits"
            name="benefits"
            label="Benefits"
            value={formik.values.benefits}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.benefits && Boolean(formik.errors.benefits)}
            helperText={formik.touched.benefits && formik.errors.benefits}
            multiline
            rows={3}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="documents"
            name="documents"
            label="Required Documents"
            value={formik.values.documents}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.documents && Boolean(formik.errors.documents)}
            helperText={formik.touched.documents && formik.errors.documents}
            multiline
            rows={3}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl
            fullWidth
            error={formik.touched.category?.categoryId && Boolean(formik.errors.category?.categoryId)}
            disabled={isLoadingCategories}
          >
            <InputLabel id="scheme-category-label">Category</InputLabel>
            <Select
              labelId="scheme-category-label"
              id="category.categoryId"
              name="category.categoryId"
              value={formik.values.category.categoryId || ''}
              onChange={handleChange}
              onBlur={formik.handleBlur}
              label="Category"
              required
            >
              <MenuItem value="">
                <em>Select Category</em>
              </MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.categoryId} value={category.categoryId?.toString?.() || category.categoryId}>
                  {category.categoryName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.category?.categoryId && formik.errors.category?.categoryId && (
              <FormHelperText>{formik.errors.category.categoryId}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel id="yojana-government-label">Government</InputLabel>
            <Select
              labelId="yojana-government-label"
              id="yojanaGovernment"
              name="yojanaGovernment"
              value={formik.values.yojanaGovernment || ''}
              onChange={handleChange}
              onBlur={formik.handleBlur}
              label="Government"
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              <MenuItem value="Central">Central</MenuItem>
              <MenuItem value="State">State</MenuItem>
              <MenuItem value="Local">Local</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Yojana Video (Optional)
          </Typography>
          <Button
            variant="outlined"
            component="label"
            sx={{ mr: 2 }}
          >
            {videoFile ? 'Change Video' : 'Upload Video'}
            <input
              type="file"
              hidden
              accept="video/*"
              onChange={handleVideoChange}
            />
          </Button>
          {formik.values.yojanaVideoUrl && !videoFile && (
            <Button
              variant="text"
              component="a"
              href={formik.values.yojanaVideoUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none' }}
            >
              View Current Video
            </Button>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {videoFile
              ? `Selected file: ${videoFile.name}`
              : formik.values.yojanaVideoUrl
                ? 'Using uploaded video URL'
                : 'No video selected. This field is optional.'}
          </Typography>
          {videoUploadError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {videoUploadError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            GR Link (Optional)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              component="label"
            >
              {grLinkFile ? 'Change GR Link File' : 'Upload GR Link'}
              <input
                type="file"
                hidden
                accept="*/*"
                onChange={handleGrLinkChange}
              />
            </Button>
            {(grLinkFile || formik.values.grLink) && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleGrLinkRemove}
              >
                Remove GR Link
              </Button>
            )}
            {formik.values.grLink && !grLinkFile && (
              <Button
                variant="text"
                component="a"
                href={formik.values.grLink}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none' }}
              >
                View Current GR Link
              </Button>
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {grLinkFile
              ? `Selected file: ${grLinkFile.name}`
              : formik.values.grLink
                ? 'Using uploaded GR link'
                : 'No GR link file selected. This field is optional.'}
          </Typography>
          {grLinkUploadError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {grLinkUploadError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Form PDF (Optional)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              component="label"
            >
              {formPdfUrlFile ? 'Change Form PDF File' : 'Upload Form PDF'}
              <input
                type="file"
                hidden
                accept=".pdf,application/pdf"
                onChange={handleFormPdfUrlChange}
              />
            </Button>
            {(formPdfUrlFile || formik.values.formPdfUrl) && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleFormPdfUrlRemove}
              >
                Remove Form PDF
              </Button>
            )}
            {formik.values.formPdfUrl && !formPdfUrlFile && (
              <Button
                variant="text"
                component="a"
                href={formik.values.formPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none' }}
              >
                View Current Form PDF
              </Button>
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {formPdfUrlFile
              ? `Selected file: ${formPdfUrlFile.name}`
              : formik.values.formPdfUrl
                ? 'Using uploaded form PDF'
                : 'No form PDF selected. This field is optional.'}
          </Typography>
          {formPdfUrlUploadError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {formPdfUrlUploadError}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="pdfFormLink"
            name="pdfFormLink"
            label="PDF Form Link (Optional)"
            value={formik.values.pdfFormLink || ''}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            placeholder="https://..."
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            id="officeAddress"
            name="officeAddress"
            label="Office Address (Optional)"
            value={formik.values.officeAddress || ''}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            multiline
            minRows={2}
            placeholder="Enter office address"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="officeAddressContact"
            name="officeAddressContact"
            label="Office Contact (Optional)"
            value={formik.values.officeAddressContact || ''}
            onChange={handleChange}
            onBlur={formik.handleBlur}
            placeholder="Mobile number"
            inputProps={{ maxLength: 15, inputMode: 'tel' }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Scheme Filters
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Min Age"
            type="number"
            value={filterForm.minAge}
            onChange={handleFilterNumberChange('minAge')}
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Max Age"
            type="number"
            value={filterForm.maxAge}
            onChange={handleFilterNumberChange('maxAge')}
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            fullWidth
            options={GENDER_OPTIONS}
            value={filterForm.gender}
            onChange={(event, newValue) => updateFilterForm({ gender: newValue })}
            renderInput={(params) => (
              <TextField {...params} label="Gender" placeholder="Select gender" />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            fullWidth
            options={RATION_CARD_OPTIONS}
            value={filterForm.rationCard}
            onChange={(event, newValue) => updateFilterForm({ rationCard: newValue })}
            renderInput={(params) => (
              <TextField {...params} label="Ration Card" placeholder="Select ration card" />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={religions}
            value={resolveSelectedOption(religions, filterForm.religion, ['religionId'])}
            onChange={(event, newValue) => updateFilterForm({ religion: newValue })}
            getOptionLabel={(option) => option.religionName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.religionId) === String(value?.religionId)
            }
            renderInput={(params) => (
              <TextField {...params} label="Religion" placeholder="Search religion..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={castCategories}
            value={resolveSelectedOption(castCategories, filterForm.castCategory, ['categoryId'])}
            onChange={(event, newValue) => updateFilterForm({ castCategory: newValue })}
            getOptionLabel={(option) => option.categoryName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.categoryId) === String(value?.categoryId)
            }
            renderInput={(params) => (
              <TextField {...params} label="Cast Category" placeholder="Search cast category..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={casts}
            value={resolveSelectedOption(casts, filterForm.cast, ['castId'])}
            onChange={(event, newValue) => updateFilterForm({ cast: newValue })}
            getOptionLabel={(option) => option.castName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.castId) === String(value?.castId)
            }
            loading={isLoadingCasts}
            renderInput={(params) => (
              <TextField {...params} label="Cast" placeholder="Search cast..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={educationList}
            value={resolveSelectedOption(educationList, filterForm.education, ['educationId'])}
            onChange={(event, newValue) => updateFilterForm({ education: newValue })}
            getOptionLabel={(option) => option.educationName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.educationId) === String(value?.educationId)
            }
            loading={isLoadingEducation}
            renderInput={(params) => (
              <TextField {...params} label="Education" placeholder="Search education..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={INCOME_RANGE_OPTIONS}
            value={filterForm.incomeRange}
            onChange={(event, newValue) => updateFilterForm({ incomeRange: newValue })}
            renderInput={(params) => (
              <TextField {...params} label="Income Range" placeholder="Select income range" />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            multiple
            fullWidth
            options={professions}
            value={resolveSelectedOptions(professions, filterForm.professions, ['professionId'])}
            onChange={(event, newValue) => updateFilterForm({ professions: newValue })}
            getOptionLabel={(option) => option.professionName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.professionId) === String(value?.professionId)
            }
            renderInput={(params) => (
              <TextField {...params} label="Profession" placeholder="Search profession..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Min Farm (acres)"
            type="number"
            value={filterForm.minFarm}
            onChange={handleFilterNumberChange('minFarm')}
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Max Farm (acres)"
            type="number"
            value={filterForm.maxFarm}
            onChange={handleFilterNumberChange('maxFarm')}
            inputProps={{ min: 0 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Min Disability (%)"
            type="number"
            value={filterForm.minDisability}
            onChange={handleFilterNumberChange('minDisability')}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Max Disability (%)"
            type="number"
            value={filterForm.maxDisability}
            onChange={handleFilterNumberChange('maxDisability')}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={states}
            value={resolveSelectedOption(states, filterForm.state, ['stateId'])}
            onChange={handleStateChange}
            getOptionLabel={(option) => option.stateName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.stateId) === String(value?.stateId)
            }
            renderInput={(params) => (
              <TextField {...params} label="State" placeholder="Search state..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={districts}
            value={resolveSelectedOption(districts, filterForm.district, ['districtId'])}
            onChange={handleDistrictChange}
            getOptionLabel={(option) => option.districtName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.districtId) === String(value?.districtId)
            }
            disabled={!filterForm.state || isLoadingDistricts}
            loading={isLoadingDistricts}
            renderInput={(params) => (
              <TextField {...params} label="District" placeholder="Search district..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={talukas}
            value={resolveSelectedOption(talukas, filterForm.taluka, ['talukaId'])}
            onChange={handleTalukaChange}
            getOptionLabel={(option) => option.talukaName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.talukaId) === String(value?.talukaId)
            }
            disabled={!filterForm.district || isLoadingTalukas}
            loading={isLoadingTalukas}
            renderInput={(params) => (
              <TextField {...params} label="Taluka" placeholder="Search taluka..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Autocomplete
            fullWidth
            options={villages}
            value={resolveSelectedOption(villages, filterForm.village, ['villageId'])}
            onChange={(event, newValue) => updateFilterForm({ village: newValue })}
            getOptionLabel={(option) => option.villageName || ''}
            isOptionEqualToValue={(option, value) =>
              String(option.villageId) === String(value?.villageId)
            }
            disabled={!filterForm.taluka || isLoadingVillages}
            loading={isLoadingVillages}
            renderInput={(params) => (
              <TextField {...params} label="Village" placeholder="Search village..." />
            )}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                id="yojanaInformationCompleted"
                name="yojanaInformationCompleted"
                checked={!!formik.values.yojanaInformationCompleted}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            }
            label="Yojana information completed"
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting || uploadingVideo || uploadingGrLink || uploadingFormPdfUrl}
            sx={{ py: 1.5 }}
          >
            {isSubmitting
              ? (uploadingVideo || uploadingGrLink || uploadingFormPdfUrl)
                ? <CircularProgress size={24} />
                : <CircularProgress size={24} />
              : scheme
                ? 'Update Scheme'
                : 'Add Scheme'}
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchemeForm;
